import { FC, ReactNode, useMemo, useRef, useState } from 'react';
import { Avatar, Button, Checkbox, Col, DatePicker, Input, Row, Segmented, Space, Typography } from 'antd';
import { ListFilter } from 'lucide-react';
import {
    TbArrowRight,
    TbBraces,
    TbChartBar,
    TbChartLine,
    TbChartPie,
    TbEye,
    TbFlask,
    TbInfoCircle,
    TbMail,
    TbMessageCircle,
    TbPlus,
    TbReportAnalytics,
    TbSettings,
    TbTable,
    TbTrash,
    TbWebhook,
} from 'react-icons/tb';

import { SlackSVGIcon } from '@/assets';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useCooldown } from '@/hooks/useCooldown';
import {
    frequencyOptions,
    parseTimeOfDay,
    weekDays,
} from '@/layouts/02-Authenticated/layout/03-Content/routes/08-Relations/layout/03-Sections/views/01-Dashboards/routes/DashboardDetail/components/ScheduleReportsModal/components/Scheduling/configuration';
import {
    AlertIcon,
    DiagramDetailSider,
    DropdownButtonSelect,
    FilterMatchingPatternSelect,
    FilterRowEmptyState,
    FilterRowSurface,
    LearnMoreFooter,
    PlatformDatePicker,
    ScheduleOptionDropdown,
    TimezoneDropdown,
} from '@/shared';
import { PlatformAlert } from '@/shared/components/alerts';
import SelectableOptionCard from '@/shared/components/cards/SelectableOptionCard';
import { PlatformForm as Form } from '@/shared/components/forms';
import InlineIconLabel from '@/shared/components/modals/ResourceCreationModals/CreateOAuthApplicationModal/components/InlineIconLabel';
import { useUserPreferences } from '@/stores/database/entities/00-RBAC/99-UserPreferences';
import {
    AlertCatalog,
    AlertCategory,
    AlertCondition,
    AlertDeliveryDestination,
    AlertDeliveryMethod,
    AlertFrequency,
    AlertInput,
    AlertMessageTemplates,
    AlertOperator,
    AlertSchedule,
    AlertTemplateKey,
    AlertTestResult,
    AlertVisualizationType,
    WorkspaceAlert,
} from '@/types';
import { dayjs, nanoId } from '@/utilities';

import { isNotificationChannelEnabled, PreferenceNotificationChannel } from '../../notificationChannelPolicy';
import AlertCategoryDropdown, { alertCategoryIcons } from './components/AlertCategoryDropdown';
import AlertConditionValueInput from './components/AlertConditionValueInput';
import AlertDeliveryDestinationSection from './components/AlertDeliveryDestinationSection';
import AlertFlowNodeInspector, { AlertFlowNodeDetail } from './components/AlertFlowNodeInspector';
import AlertFlowPreview, { AlertFlowExpansionConfig } from './components/AlertFlowPreview';
import AlertFormSection from './components/AlertFormSection';
import AlertFormSteps from './components/AlertFormSteps';
import AlertMessageTemplateEditor from './components/AlertMessageTemplateEditor';
import AlertReportDestinationSection, { AlertReportDestination } from './components/AlertReportDestinationSection';
import AlertRequiredFieldsPreviewPopover from './components/AlertRequiredFieldsPreviewPopover';
import { isAlertConditionValueComplete } from './conditionValues';
import { AlertRequiredField, alertRequiredFieldDetails } from './requiredFields';
import { AlertFormSectionKey } from './steps';

import './styles.css';

interface AlertFormProps {
    alert?: WorkspaceAlert;
    catalog: AlertCatalog;
    initialInput?: Partial<AlertInput>;
    saving: boolean;
    testing: boolean;
    onCancel: () => void;
    onSubmit: (input: AlertInput) => void;
    onSurfaceChange?: (surface: AlertFormSurface) => void;
    onTest: (input: AlertInput) => Promise<AlertTestResult | undefined>;
    showSurfaceSwitcher?: boolean;
    surface?: AlertFormSurface;
}

interface IdentityFields {
    description?: string;
    name: string;
    webhookSecret?: string;
    webhookUrl?: string;
}

export type AlertFormSurface = 'configuration' | 'preview';

const TEST_ALERT_UI_COOLDOWN_SECONDS = 30;

const initialOpenSections: Record<AlertFormSectionKey, boolean> = {
    identity: true,
    templates: false,
    query: false,
    delivery: false,
    schedule: false,
};

const deliveryOptions: Array<{ description: string; icon: ReactNode; label: string; value: AlertDeliveryMethod }> = [
    { value: 'inApp', label: 'In-App', description: 'Deliver to the workspace inbox.', icon: <TbMessageCircle /> },
    { value: 'email', label: 'Email', description: 'Send to your verified email address.', icon: <TbMail /> },
    { value: 'push', label: 'Push', description: 'Notify supported desktop and mobile clients.', icon: <AlertIcon /> },
    {
        value: 'slack',
        label: 'Slack',
        description: 'Use your configured Slack notification channel.',
        icon: <img alt='' aria-hidden src={SlackSVGIcon} style={{ height: '1rem', width: '1rem' }} />,
    },
    {
        value: 'webhook',
        label: 'Webhook',
        description: 'Send a signed event to an external HTTPS endpoint.',
        icon: <TbWebhook />,
    },
    {
        value: 'reportComponent',
        label: 'Report Component',
        description: 'Render matched alert data in a reusable report component.',
        icon: <TbReportAnalytics />,
    },
];

const deliveryMethodsRequiringConfiguration = new Set<AlertDeliveryMethod>(['reportComponent', 'slack', 'webhook']);

const visualizationOptions = [
    { value: 'table', label: 'Table', description: 'Structured rows and columns.', icon: <TbTable /> },
    { value: 'bar', label: 'Bar Chart', description: 'Compare discrete categories.', icon: <TbChartBar /> },
    { value: 'line', label: 'Line Chart', description: 'Show change over time.', icon: <TbChartLine /> },
    { value: 'pie', label: 'Pie Chart', description: 'Show categorical composition.', icon: <TbChartPie /> },
    {
        value: 'json',
        label: 'JSON',
        description: 'Expose structured alert data for advanced report components.',
        icon: <TbBraces />,
    },
] satisfies Array<{ value: AlertVisualizationType; label: string; description: string; icon: ReactNode }>;

const defaultSchedule = (): AlertSchedule => ({
    frequency: 'day',
    timeOfDay: '08:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
    weekDays: [1, 2, 3, 4, 5],
    startDate: null,
    endDate: null,
});

const operatorsByType = {
    date: ['withinDays', 'before', 'after', 'between', 'changed'],
    enum: ['equals', 'notEquals', 'in', 'changed'],
    number: ['equals', 'gt', 'gte', 'lt', 'lte', 'between', 'changed'],
    string: ['equals', 'notEquals', 'contains', 'startsWith', 'in', 'changed'],
} satisfies Record<'date' | 'enum' | 'number' | 'string', AlertOperator[]>;

const operatorLabels: Record<AlertOperator, string> = {
    after: 'is after',
    before: 'is before',
    between: 'is between',
    changed: 'changes',
    contains: 'contains',
    equals: 'equals',
    gt: 'is greater than',
    gte: 'is at least',
    in: 'is one of',
    lt: 'is less than',
    lte: 'is at most',
    notEquals: 'does not equal',
    startsWith: 'starts with',
    withinDays: 'within days',
};

const AlertForm: FC<AlertFormProps> = ({
    alert,
    catalog,
    initialInput,
    onCancel,
    onSubmit,
    onSurfaceChange,
    onTest,
    saving,
    showSurfaceSwitcher = true,
    surface: controlledSurface,
    testing,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { preferences } = useUserPreferences();
    const [form] = Form.useForm<IdentityFields>();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [internalSurface, setInternalSurface] = useState<AlertFormSurface>('configuration');
    const surface = controlledSurface ?? internalSurface;
    const setSurface = (nextSurface: AlertFormSurface) => {
        setInternalSurface(nextSurface);
        onSurfaceChange?.(nextSurface);
    };
    const [selectedPreviewNode, setSelectedPreviewNode] = useState<AlertFlowNodeDetail>();
    const [previewInspectorOpen, setPreviewInspectorOpen] = useState(true);
    const [openSections, setOpenSections] = useState<Record<AlertFormSectionKey, boolean>>(initialOpenSections);
    const [category, setCategory] = useState<AlertCategory>(alert?.category ?? initialInput?.category ?? 'companies');
    const [templateKey, setTemplateKey] = useState<AlertTemplateKey | undefined>(
        alert?.templateKey ?? initialInput?.templateKey ?? undefined,
    );
    const [conditions, setConditions] = useState<AlertCondition[]>(
        alert?.query.conditions ?? initialInput?.query?.conditions ?? [],
    );
    const [match, setMatch] = useState<'all' | 'any'>(alert?.query.match ?? initialInput?.query?.match ?? 'all');
    const [deliveryMethods, setDeliveryMethods] = useState<AlertDeliveryMethod[]>(
        alert?.deliveryMethods ?? initialInput?.deliveryMethods ?? ['inApp'],
    );
    const [deliveryDestinations, setDeliveryDestinations] = useState<AlertDeliveryDestination[]>(
        alert?.deliveryDestinations ?? initialInput?.deliveryDestinations ?? [],
    );
    const [messageTemplates, setMessageTemplates] = useState<AlertMessageTemplates>(
        alert?.messageTemplates ?? initialInput?.messageTemplates ?? {},
    );
    const [schedule, setSchedule] = useState<AlertSchedule>(
        alert?.schedule ?? initialInput?.schedule ?? defaultSchedule(),
    );
    const [visualization, setVisualization] = useState<AlertVisualizationType>(
        alert?.reportComponent?.visualization ?? initialInput?.reportComponent?.visualization ?? 'table',
    );
    const [reportDestination, setReportDestination] = useState<AlertReportDestination | undefined>(() => {
        const reportComponent = alert?.reportComponent ?? initialInput?.reportComponent;

        return reportComponent?.dashboardId && reportComponent.reportId
            ? { dashboardId: reportComponent.dashboardId, reportId: reportComponent.reportId }
            : undefined;
    });
    const [preservedIdentityValues, setPreservedIdentityValues] = useState<IdentityFields>(() => ({
        description: alert?.description ?? initialInput?.description ?? undefined,
        name: alert?.name ?? initialInput?.name ?? '',
        webhookSecret: undefined,
        webhookUrl: alert?.webhook?.url,
    }));
    const [queryError, setQueryError] = useState<string>();
    const {
        coolingDown: testCoolingDown,
        remainingSeconds: testCooldownSeconds,
        start: startTestCooldown,
    } = useCooldown({ seconds: TEST_ALERT_UI_COOLDOWN_SECONDS });
    const watchedName = Form.useWatch('name', { form, preserve: true });
    const watchedDescription = Form.useWatch('description', { form, preserve: true });
    const watchedWebhookUrl = Form.useWatch('webhookUrl', { form, preserve: true });
    const watchedWebhookSecret = Form.useWatch('webhookSecret', { form, preserve: true });
    const name = watchedName ?? preservedIdentityValues.name;
    const description = watchedDescription ?? preservedIdentityValues.description;
    const webhookUrl = watchedWebhookUrl ?? preservedIdentityValues.webhookUrl;
    const webhookSecret = watchedWebhookSecret ?? preservedIdentityValues.webhookSecret;
    const selectedCategory = catalog.categories.find((entry) => entry.key === category) ?? catalog.categories[0];
    const categoryTemplates = useMemo(
        () => catalog.templates.filter((template) => template.category === category),
        [catalog.templates, category],
    );

    const fieldsByKey = useMemo(
        () => new Map(selectedCategory?.fields.map((field) => [field.key, field]) ?? []),
        [selectedCategory],
    );

    const conditionLabels = useMemo(
        () =>
            conditions.map((condition) => {
                const field = fieldsByKey.get(condition.field)?.label ?? condition.field;
                const value = Array.isArray(condition.value) ? condition.value.join(', ') : String(condition.value);

                return `${field} ${operatorLabels[condition.operator]}${condition.operator === 'changed' ? '' : ` ${value}`}`;
            }),
        [conditions, fieldsByKey],
    );

    const deliveryLabels = deliveryMethods.map((method) => {
        const label = deliveryOptions.find((option) => option.value === method)?.label ?? method;
        if (method !== 'slack') {
            return label;
        }

        const channelCount =
            deliveryDestinations.find((destination) => destination.provider === 'slack')?.destinationIds.length ?? 0;

        return `${label} · ${channelCount} ${channelCount === 1 ? 'channel' : 'channels'}`;
    });
    const preferenceGovernedMethods = new Set<PreferenceNotificationChannel>(['email', 'inApp', 'push', 'slack']);
    const disabledDeliveryMethods = deliveryMethods.filter(
        (method): method is PreferenceNotificationChannel =>
            preferenceGovernedMethods.has(method as PreferenceNotificationChannel) &&
            !isNotificationChannelEnabled({
                channel: method as PreferenceNotificationChannel,
                preferences,
                workflowKey: 'workspace_alert_triggered',
            }),
    );
    const identityComplete = Boolean(name?.trim());
    const queryComplete =
        conditions.length > 0 &&
        conditions.every((condition) =>
            isAlertConditionValueComplete(condition, fieldsByKey.get(condition.field)?.type),
        );
    const scheduleComplete = Boolean(schedule.timeOfDay && schedule.timezone);
    const missingRequiredFields: AlertRequiredField[] = [];

    if (!identityComplete) {
        missingRequiredFields.push('name');
    }
    if (!queryComplete) {
        missingRequiredFields.push('query');
    }
    if (!deliveryMethods.length) {
        missingRequiredFields.push('delivery');
    }
    if (
        deliveryMethods.includes('slack') &&
        !deliveryDestinations.some(
            (destination) => destination.provider === 'slack' && destination.destinationIds.length,
        )
    ) {
        missingRequiredFields.push('slackChannels');
    }
    if (deliveryMethods.includes('webhook') && !webhookUrl?.trim()) {
        missingRequiredFields.push('webhookUrl');
    }
    if (deliveryMethods.includes('webhook') && !alert?.hasWebhookSecret && (webhookSecret?.trim().length ?? 0) < 16) {
        missingRequiredFields.push('webhookSecret');
    }
    if (deliveryMethods.includes('reportComponent') && !reportDestination?.dashboardId) {
        missingRequiredFields.push('reportDashboard');
    }
    if (deliveryMethods.includes('reportComponent') && !reportDestination?.reportId) {
        missingRequiredFields.push('reportDestination');
    }
    if (!scheduleComplete) {
        missingRequiredFields.push('schedule');
    }

    const previewReady = missingRequiredFields.length === 0;

    const addCondition = (field: string, operator: AlertOperator = 'equals', value: number | string = '') => {
        setConditions((current) => [...current, { field, id: nanoId(12, true), operator, value }]);
        setQueryError(undefined);
    };

    const applyTemplate = (key: AlertTemplateKey) => {
        const template = catalog.templates.find((entry) => entry.key === key);
        if (!template) {
            return;
        }
        setTemplateKey(key);
        setCategory(template.category);
        setMatch(template.query.match);
        setConditions(template.query.conditions.map((condition) => ({ ...condition, id: nanoId(12, true) })));
        setQueryError(undefined);
        setOpenSections((current) => ({ ...current, query: true }));
    };

    const updateCondition = (id: string, patch: Partial<AlertCondition>) => {
        setConditions((current) =>
            current.map((condition) => (condition.id === id ? { ...condition, ...patch } : condition)),
        );
        setQueryError(undefined);
    };

    const toggleDelivery = (method: AlertDeliveryMethod, checked: boolean) => {
        setDeliveryMethods((current) =>
            checked ? Array.from(new Set([...current, method])) : current.filter((value) => value !== method),
        );
        if (!checked && method === 'slack') {
            setDeliveryDestinations((current) => current.filter((destination) => destination.provider !== 'slack'));
        }
        if (!checked && method === 'reportComponent') {
            setReportDestination(undefined);
        }
        if (!checked && (method === 'email' || method === 'inApp' || method === 'push' || method === 'slack')) {
            setMessageTemplates((current) => {
                const next = { ...current };
                delete next[method];
                return next;
            });
        }
    };

    const addConditionForField = (fieldKey: string) => {
        const field = fieldsByKey.get(fieldKey);
        if (!field) {
            return;
        }

        const recommendation = selectedCategory?.recommendations.find((entry) => entry.field === fieldKey);
        if (recommendation) {
            addCondition(recommendation.field, recommendation.operator, recommendation.value);
            return;
        }

        addCondition(field.key, 'changed', '');
    };

    const conditionExpansion: AlertFlowExpansionConfig = {
        ariaLabel: 'Add trigger condition',
        menuLabel: 'Add Trigger Condition',
        options: (selectedCategory?.fields ?? []).map((field) => ({
            description: `Add ${field.label} as another governed trigger condition.`,
            icon: <ListFilter size='0.875rem' />,
            key: field.key,
            label: field.label,
            onSelect: () => addConditionForField(field.key),
        })),
    };

    const deliveryExpansion: AlertFlowExpansionConfig = {
        ariaLabel: 'Add delivery destination',
        menuLabel: 'Add Delivery Method',
        options: deliveryOptions
            .filter(
                (option) => catalog.deliveryMethods.includes(option.value) && !deliveryMethods.includes(option.value),
            )
            .map((option) => ({
                description: option.description,
                icon: option.icon,
                key: option.value,
                label: option.label,
                onSelect: () => toggleDelivery(option.value, true),
                requiresConfiguration: deliveryMethodsRequiringConfiguration.has(option.value),
            })),
    };

    const missingRequiredFieldSet = new Set(missingRequiredFields);
    const deliveryRequiredFields = deliveryMethods.map((method): AlertRequiredField[] => {
        if (method === 'slack') {
            return missingRequiredFieldSet.has('slackChannels') ? ['slackChannels'] : [];
        }
        if (method === 'webhook') {
            return (['webhookUrl', 'webhookSecret'] as AlertRequiredField[]).filter((field) =>
                missingRequiredFieldSet.has(field),
            );
        }
        if (method === 'reportComponent') {
            return (['reportDashboard', 'reportDestination'] as AlertRequiredField[]).filter((field) =>
                missingRequiredFieldSet.has(field),
            );
        }

        return [];
    });

    const updateDeliveryDestination = (
        provider: AlertDeliveryDestination['provider'],
        destination?: AlertDeliveryDestination,
    ) =>
        setDeliveryDestinations((current) =>
            destination
                ? [...current.filter((entry) => entry.provider !== provider), destination]
                : current.filter((entry) => entry.provider !== provider),
        );

    const toggleSection = (section: AlertFormSectionKey) =>
        setOpenSections((current) => ({ ...current, [section]: !current[section] }));

    const selectStep = (section: AlertFormSectionKey) => {
        setSurface('configuration');
        setOpenSections((current) => ({ ...current, [section]: true }));
        requestAnimationFrame(() => {
            document.querySelector(`#alert-form-${section}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    };

    const selectRequiredField = (field: AlertRequiredField) => {
        const details = alertRequiredFieldDetails[field];
        const fieldIds: Partial<Record<AlertRequiredField, string>> = {
            name: 'name',
            slackChannels: 'alert-delivery-destination-slack',
            reportDashboard: 'alert-report-component-destination',
            reportDestination: 'alert-report-component-destination',
            webhookSecret: 'webhookSecret',
            webhookUrl: 'webhookUrl',
        };

        selectStep(details.sectionKey);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const fieldId = fieldIds[field];
                if (!fieldId) {
                    return;
                }
                const fieldElement = document.querySelector<HTMLElement>(`#${fieldId}`);
                fieldElement?.focus({ preventScroll: true });
                fieldElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        });
    };

    const buildInput = async (): Promise<AlertInput | undefined> => {
        let values: IdentityFields;
        try {
            values = await form.validateFields();
        } catch {
            setSurface('configuration');
            setOpenSections((current) => ({ ...current, identity: true, delivery: true }));
            return undefined;
        }

        if (!queryComplete) {
            setSurface('configuration');
            setOpenSections((current) => ({ ...current, query: true }));
            setQueryError('Complete at least one valid query condition before saving this alert.');
            return undefined;
        }

        return {
            category,
            deliveryDestinations: deliveryDestinations.filter((destination) =>
                deliveryMethods.includes(destination.provider),
            ),
            deliveryMethods,
            description: values.description?.trim() || null,
            enabled: alert?.enabled ?? initialInput?.enabled ?? true,
            messageTemplates: Object.fromEntries(
                Object.entries(messageTemplates).filter(([channel]) =>
                    deliveryMethods.includes(channel as AlertDeliveryMethod),
                ),
            ),
            name: values.name.trim(),
            query: { conditions, match, version: 1 },
            reportComponent:
                deliveryMethods.includes('reportComponent') &&
                reportDestination?.dashboardId &&
                reportDestination.reportId
                    ? { ...reportDestination, visualization }
                    : undefined,
            schedule,
            templateKey,
            webhook:
                deliveryMethods.includes('webhook') && values.webhookUrl
                    ? { url: values.webhookUrl.trim(), secret: values.webhookSecret?.trim() || undefined }
                    : undefined,
        };
    };

    const submit = async () => {
        const input = await buildInput();
        if (input) {
            onSubmit(input);
        }
    };

    const test = async () => {
        const input = await buildInput();
        if (input) {
            const result = await onTest(input);
            if (result?.valid) {
                startTestCooldown();
            }
        }
    };

    const previewLabel = (
        <InlineIconLabel
            icon={<TbEye />}
            label={
                <span style={{ alignItems: 'center', display: 'inline-flex', gap: '0.375rem', height: '100%' }}>
                    Preview
                    {!previewReady ? (
                        <AlertRequiredFieldsPreviewPopover
                            fields={missingRequiredFields}
                            onSelect={selectRequiredField}
                        />
                    ) : null}
                </span>
            }
        />
    );

    return (
        <Form
            form={form}
            className='alert-create-form'
            initialValues={preservedIdentityValues}
            layout='vertical'
            preserve
            style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
            onValuesChange={(_, values: IdentityFields) => {
                setPreservedIdentityValues((current) => ({ ...current, ...values }));
            }}
        >
            {showSurfaceSwitcher ? (
                <Row align='middle' style={{ marginBottom: '0.75rem' }}>
                    <Col flex='none'>
                        <Segmented<AlertFormSurface>
                            className='alert-create-segmented'
                            value={surface}
                            options={[
                                {
                                    value: 'configuration',
                                    label: <InlineIconLabel icon={<TbSettings />} label='Configuration' />,
                                },
                                {
                                    value: 'preview',
                                    label: previewLabel,
                                },
                            ]}
                            onChange={setSurface}
                        />
                    </Col>
                    <Col flex='auto' />
                </Row>
            ) : null}

            {surface === 'configuration' ? (
                <div
                    style={{
                        border: `1px dashed ${token.colorBorder}`,
                        borderRadius: token.borderRadiusLG,
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                    }}
                >
                    <Row wrap={false} style={{ height: '100%' }}>
                        <Col
                            ref={scrollContainerRef}
                            flex='auto'
                            style={{ minWidth: 0, overflowX: 'hidden', overflowY: 'auto', scrollBehavior: 'smooth' }}
                        >
                            <AlertFormSection
                                description='Name the alert for auditability and explain the decision workflow it supports.'
                                sectionKey='identity'
                                open={openSections.identity}
                                onToggle={toggleSection}
                            >
                                <Row gutter={12}>
                                    <Col span={12}>
                                        <Form.Item
                                            name='name'
                                            label='Name'
                                            rules={[{ required: true, message: 'Enter an alert name.' }, { max: 160 }]}
                                        >
                                            <Input placeholder='e.g. Material earnings variance...' />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label='Category'>
                                            <AlertCategoryDropdown
                                                categories={catalog.categories}
                                                value={category}
                                                onChange={(value) => {
                                                    setCategory(value);
                                                    setTemplateKey(undefined);
                                                    setConditions([]);
                                                    setQueryError(undefined);
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Form.Item name='description' label='Description' rules={[{ max: 1000 }]}>
                                    <Input.TextArea
                                        autoSize={{ minRows: 4 }}
                                        placeholder='Describe Who Should Act And Why This Alert Matters...'
                                        style={{ resize: 'none' }}
                                    />
                                </Form.Item>
                            </AlertFormSection>

                            <AlertFormSection
                                description={`Start with one of six ${selectedCategory?.label ?? category} templates, then refine its governed event signature below.`}
                                sectionKey='templates'
                                open={openSections.templates}
                                onToggle={toggleSection}
                            >
                                <PlatformAlert
                                    status='info'
                                    title='Templates follow the selected category'
                                    description={`These six templates are based on the ${selectedCategory?.label ?? category} category selected above. Changing the category refreshes this set and clears category-specific query conditions.`}
                                    style={{ margin: '0.5rem 0 1rem' }}
                                />
                                <Row gutter={[12, 12]}>
                                    {categoryTemplates.map((template) => (
                                        <Col key={template.key} span={12}>
                                            <SelectableOptionCard
                                                borderStyle={templateKey === template.key ? 'solid' : 'dashed'}
                                                description={template.description}
                                                icon={
                                                    <Avatar
                                                        shape='square'
                                                        icon={alertCategoryIcons[template.category]}
                                                        size='small'
                                                        style={{
                                                            backgroundColor: token.colorFillSecondary,
                                                            border: `1px solid ${token.colorBorder}`,
                                                            color: token.colorTextTertiary,
                                                        }}
                                                    />
                                                }
                                                label={<strong>{template.label}</strong>}
                                                selected={templateKey === template.key}
                                                style={{ minHeight: '6rem' }}
                                                onSelect={() => applyTemplate(template.key)}
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            </AlertFormSection>

                            <AlertFormSection
                                description='Construct the versioned event signature that determines when this alert fires.'
                                sectionKey='query'
                                open={openSections.query}
                                headerExtra={
                                    <FilterMatchingPatternSelect
                                        value={match}
                                        style={{ minWidth: '7rem', width: '7rem' }}
                                        onChange={setMatch}
                                    />
                                }
                                onToggle={toggleSection}
                            >
                                <section
                                    style={{
                                        border: `1px dashed ${token.colorBorder}`,
                                        borderRadius: token.borderRadius,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <div style={{ minWidth: 0, overflowX: 'auto' }}>
                                        <FilterRowSurface horizontalPadding='0.75rem'>
                                            <FilterRowEmptyState
                                                hint={`Recommended ${selectedCategory?.label ?? ''} filters`}
                                                recommendations={(selectedCategory?.recommendations ?? []).map(
                                                    (recommendation) => ({
                                                        key: recommendation.label,
                                                        label: recommendation.label,
                                                        icon: alertCategoryIcons[category],
                                                        onClick: () =>
                                                            addCondition(
                                                                recommendation.field,
                                                                recommendation.operator,
                                                                recommendation.value,
                                                            ),
                                                    }),
                                                )}
                                            />
                                        </FilterRowSurface>
                                    </div>
                                    <div style={{ overflowX: 'auto', padding: '0.75rem' }}>
                                        <Space direction='vertical' size={8} style={{ width: '100%' }}>
                                            {conditions.map((condition) => {
                                                const field = fieldsByKey.get(condition.field);
                                                const fieldType = field?.type ?? 'string';

                                                return (
                                                    <div
                                                        key={condition.id}
                                                        style={{
                                                            alignItems: 'stretch',
                                                            display: 'grid',
                                                            gap: '0.375rem',
                                                            gridTemplateColumns:
                                                                'minmax(10rem, 1.2fr) minmax(8rem, 0.8fr) minmax(10rem, 1.2fr) 2rem',
                                                            minWidth: '38rem',
                                                            paddingLeft: '1rem',
                                                            position: 'relative',
                                                            width: '100%',
                                                        }}
                                                    >
                                                        <span
                                                            aria-hidden
                                                            style={{
                                                                borderBottom: `1px solid ${token.colorBorder}`,
                                                                borderLeft: `1px solid ${token.colorBorder}`,
                                                                bottom: '50%',
                                                                left: '0.25rem',
                                                                position: 'absolute',
                                                                top: '-0.55rem',
                                                                width: '0.5rem',
                                                            }}
                                                        />
                                                        <DropdownButtonSelect<string>
                                                            size='small'
                                                            value={condition.field}
                                                            style={{ minWidth: 0, width: '100%' }}
                                                            menuWidth='20rem'
                                                            searchable
                                                            options={(selectedCategory?.fields ?? []).map((entry) => ({
                                                                value: entry.key,
                                                                label: entry.label,
                                                            }))}
                                                            onChange={(value) =>
                                                                value &&
                                                                updateCondition(condition.id, {
                                                                    field: value,
                                                                    operator: 'equals',
                                                                    value: '',
                                                                })
                                                            }
                                                        />
                                                        <DropdownButtonSelect<AlertOperator>
                                                            size='small'
                                                            value={condition.operator}
                                                            style={{ minWidth: 0, width: '100%' }}
                                                            menuWidth='15rem'
                                                            options={operatorsByType[fieldType].map((operator) => ({
                                                                value: operator,
                                                                label: operatorLabels[operator],
                                                            }))}
                                                            onChange={(value) =>
                                                                value &&
                                                                updateCondition(condition.id, {
                                                                    operator: value,
                                                                    value:
                                                                        value === 'between'
                                                                            ? fieldType === 'number'
                                                                                ? [0, 0]
                                                                                : ['', '']
                                                                            : value === 'changed'
                                                                              ? ''
                                                                              : condition.value,
                                                                })
                                                            }
                                                        />
                                                        <AlertConditionValueInput
                                                            condition={condition}
                                                            fieldType={fieldType}
                                                            onChange={(value) =>
                                                                updateCondition(condition.id, { value })
                                                            }
                                                        />
                                                        <Button
                                                            size='small'
                                                            aria-label='Remove condition'
                                                            icon={<TbTrash />}
                                                            style={{ width: '2rem' }}
                                                            onClick={() =>
                                                                setConditions((current) =>
                                                                    current.filter((item) => item.id !== condition.id),
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                );
                                            })}
                                            {queryError ? (
                                                <Typography.Text type='danger'>
                                                    <small>{queryError}</small>
                                                </Typography.Text>
                                            ) : null}
                                            <Button
                                                size='small'
                                                type='dashed'
                                                icon={<TbPlus />}
                                                onClick={() => {
                                                    const first = selectedCategory?.fields[0];
                                                    if (first) {
                                                        addCondition(first.key);
                                                    }
                                                }}
                                            >
                                                Add Condition
                                            </Button>
                                        </Space>
                                    </div>
                                </section>
                            </AlertFormSection>

                            <AlertFormSection
                                description='Choose one or more permission-aware destinations for matched events.'
                                sectionKey='delivery'
                                open={openSections.delivery}
                                onToggle={toggleSection}
                            >
                                <Row gutter={[8, 8]}>
                                    {deliveryOptions.map((option) => (
                                        <Col key={option.value} span={8}>
                                            <div
                                                style={{
                                                    border: `1px dashed ${token.colorBorder}`,
                                                    borderRadius: token.borderRadius,
                                                    minHeight: '4.75rem',
                                                    padding: '0.625rem',
                                                }}
                                            >
                                                <div
                                                    style={{ alignItems: 'flex-start', display: 'flex', gap: '0.5rem' }}
                                                >
                                                    <Checkbox
                                                        checked={deliveryMethods.includes(option.value)}
                                                        style={{ marginTop: '0.25rem' }}
                                                        onChange={(event) =>
                                                            toggleDelivery(option.value, event.target.checked)
                                                        }
                                                    />
                                                    <Avatar
                                                        shape='square'
                                                        icon={option.icon}
                                                        size='small'
                                                        style={{
                                                            backgroundColor: token.colorFillSecondary,
                                                            border: `1px solid ${token.colorBorder}`,
                                                            color: token.colorTextTertiary,
                                                            flex: 'none',
                                                        }}
                                                    />
                                                    <div style={{ minWidth: 0 }}>
                                                        <strong>{option.label}</strong>
                                                        <div>
                                                            <small style={{ color: token.colorTextTertiary }}>
                                                                {option.description}
                                                            </small>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                                {disabledDeliveryMethods.length ? (
                                    <PlatformAlert
                                        showIcon
                                        type='warning'
                                        icon={<TbInfoCircle />}
                                        title='Some selected methods are currently disabled'
                                        description={
                                            <span>
                                                {disabledDeliveryMethods
                                                    .map(
                                                        (method) =>
                                                            deliveryOptions.find((option) => option.value === method)
                                                                ?.label ?? method,
                                                    )
                                                    .join(', ')}{' '}
                                                will remain configured on this alert, but delivery will resume only when
                                                enabled in{' '}
                                                <a
                                                    href='/settings/account/notifications/general'
                                                    rel='noreferrer'
                                                    target='_blank'
                                                >
                                                    General Notification Settings
                                                </a>
                                                .
                                            </span>
                                        }
                                        style={{ marginTop: '0.75rem' }}
                                    />
                                ) : null}
                                <AlertMessageTemplateEditor
                                    deliveryMethods={deliveryMethods}
                                    value={messageTemplates}
                                    onChange={setMessageTemplates}
                                />
                                {deliveryMethods.includes('slack') ? (
                                    <AlertDeliveryDestinationSection
                                        provider='slack'
                                        value={deliveryDestinations.find(
                                            (destination) => destination.provider === 'slack',
                                        )}
                                        onChange={(destination) => updateDeliveryDestination('slack', destination)}
                                    />
                                ) : null}
                                {deliveryMethods.includes('webhook') ? (
                                    <section
                                        style={{
                                            border: `1px dashed ${token.colorBorder}`,
                                            borderRadius: token.borderRadius,
                                            marginTop: '0.75rem',
                                            padding: '0.75rem',
                                        }}
                                    >
                                        <Typography.Title level={5}>Signed Webhook</Typography.Title>
                                        <Row gutter={12}>
                                            <Col span={14}>
                                                <Form.Item
                                                    name='webhookUrl'
                                                    label='Endpoint URL'
                                                    rules={[{ required: true }, { type: 'url' }]}
                                                >
                                                    <Input
                                                        prefix={<TbWebhook />}
                                                        placeholder='https://example.com/webhooks/countercyclical...'
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={10}>
                                                <Form.Item
                                                    name='webhookSecret'
                                                    label='Signing Secret'
                                                    rules={[{ required: !alert?.hasWebhookSecret, min: 16 }]}
                                                >
                                                    <Input.Password
                                                        placeholder={
                                                            alert?.hasWebhookSecret
                                                                ? 'Leave Blank To Keep Existing Secret...'
                                                                : 'At Least 16 Characters...'
                                                        }
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </section>
                                ) : null}
                                {deliveryMethods.includes('reportComponent') ? (
                                    <>
                                        <AlertReportDestinationSection
                                            value={reportDestination}
                                            onChange={setReportDestination}
                                        />
                                        <section
                                            style={{
                                                border: `1px dashed ${token.colorBorder}`,
                                                borderRadius: token.borderRadius,
                                                marginTop: '0.75rem',
                                                padding: '0.75rem',
                                            }}
                                        >
                                            <Typography.Title level={5} style={{ marginTop: 0 }}>
                                                Report Component Rendering
                                            </Typography.Title>
                                            <DropdownButtonSelect<AlertVisualizationType>
                                                menuWidth='20rem'
                                                value={visualization}
                                                options={visualizationOptions}
                                                onChange={(value) => value && setVisualization(value)}
                                            />
                                        </section>
                                    </>
                                ) : null}
                            </AlertFormSection>

                            <AlertFormSection
                                description='Reuse the platform delivery cadence, timezone, and date controls to bound alert evaluation.'
                                sectionKey='schedule'
                                open={openSections.schedule}
                                onToggle={toggleSection}
                            >
                                <section
                                    style={{
                                        border: `1px dashed ${token.colorBorder}`,
                                        borderRadius: token.borderRadius,
                                        padding: '0.75rem',
                                    }}
                                >
                                    <Row gutter={12}>
                                        <Col span={12}>
                                            <small style={{ color: token.colorTextTertiary }}>Every…</small>
                                            <ScheduleOptionDropdown<AlertFrequency>
                                                value={schedule.frequency}
                                                options={frequencyOptions}
                                                onChange={(frequency) =>
                                                    setSchedule((current) => ({ ...current, frequency }))
                                                }
                                            />
                                        </Col>
                                        <Col span={12}>
                                            <small style={{ color: token.colorTextTertiary }}>At…</small>
                                            <DatePicker.TimePicker
                                                use12Hours
                                                format='h:mm a'
                                                value={parseTimeOfDay(schedule.timeOfDay)}
                                                style={{ width: '100%' }}
                                                onChange={(value) =>
                                                    setSchedule((current) => ({
                                                        ...current,
                                                        timeOfDay: value?.format('HH:mm') ?? '08:00',
                                                    }))
                                                }
                                            />
                                        </Col>
                                    </Row>
                                    {schedule.frequency === 'week' ? (
                                        <Row gutter={6} style={{ marginTop: '0.75rem' }}>
                                            {weekDays.map((day) => (
                                                <Col key={day.value} flex='1'>
                                                    <Button
                                                        block
                                                        size='small'
                                                        type={
                                                            schedule.weekDays.includes(day.value)
                                                                ? 'primary'
                                                                : 'default'
                                                        }
                                                        onClick={() =>
                                                            setSchedule((current) => {
                                                                const selected = current.weekDays.includes(day.value);
                                                                const next = selected
                                                                    ? current.weekDays.filter(
                                                                          (value) => value !== day.value,
                                                                      )
                                                                    : [...current.weekDays, day.value].sort();
                                                                return {
                                                                    ...current,
                                                                    weekDays: next.length ? next : [day.value],
                                                                };
                                                            })
                                                        }
                                                    >
                                                        {day.label}
                                                    </Button>
                                                </Col>
                                            ))}
                                        </Row>
                                    ) : null}
                                    <Row gutter={12} style={{ marginTop: '0.75rem' }}>
                                        <Col span={8}>
                                            <small style={{ color: token.colorTextTertiary }}>Start Date</small>
                                            <PlatformDatePicker
                                                value={schedule.startDate ? dayjs(schedule.startDate) : null}
                                                style={{ width: '100%' }}
                                                onChange={(value) =>
                                                    setSchedule((current) => ({
                                                        ...current,
                                                        startDate: value?.format('YYYY-MM-DD') ?? null,
                                                    }))
                                                }
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <small style={{ color: token.colorTextTertiary }}>End Date</small>
                                            <PlatformDatePicker
                                                value={schedule.endDate ? dayjs(schedule.endDate) : null}
                                                style={{ width: '100%' }}
                                                onChange={(value) =>
                                                    setSchedule((current) => ({
                                                        ...current,
                                                        endDate: value?.format('YYYY-MM-DD') ?? null,
                                                    }))
                                                }
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <small style={{ color: token.colorTextTertiary }}>Timezone</small>
                                            <TimezoneDropdown
                                                value={schedule.timezone}
                                                onChange={(timezone) =>
                                                    setSchedule((current) => ({ ...current, timezone }))
                                                }
                                            />
                                        </Col>
                                    </Row>
                                </section>
                            </AlertFormSection>

                            <Row
                                align='middle'
                                gutter={[16, 12]}
                                style={{ borderTop: `1px solid ${token.colorBorder}`, padding: '1rem' }}
                            >
                                <Col flex='auto'>
                                    <LearnMoreFooter align='left' topic='alerts and alert delivery' />
                                </Col>
                                <Col flex='none'>
                                    <Space>
                                        <Button onClick={onCancel}>Cancel</Button>
                                        <Button
                                            disabled={!previewReady || saving || testing || testCoolingDown}
                                            icon={<TbFlask />}
                                            loading={testing}
                                            onClick={() => void test()}
                                        >
                                            {testCoolingDown ? `Test again in ${testCooldownSeconds}s` : 'Test Alert'}
                                        </Button>
                                        <Button
                                            type='primary'
                                            loading={saving}
                                            disabled={!previewReady || saving || testing}
                                            onClick={() => void submit()}
                                        >
                                            {alert ? 'Save Alert' : 'Create Alert'}
                                            <TbArrowRight />
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Col>
                        <Col
                            flex='15rem'
                            style={{
                                backgroundColor: token.colorBgBase,
                                borderLeft: `1px solid ${token.colorBorderSecondary}`,
                                padding: '1rem',
                            }}
                        >
                            <AlertFormSteps scrollContainerRef={scrollContainerRef} onSelect={selectStep} />
                        </Col>
                    </Row>
                </div>
            ) : (
                <Row
                    wrap={false}
                    style={{
                        border: `1px dashed ${token.colorBorder}`,
                        borderRadius: token.borderRadiusLG,
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                    }}
                >
                    <Col flex='auto' style={{ minHeight: 0, minWidth: 0 }}>
                        <AlertFlowPreview
                            categoryLabel={selectedCategory?.label ?? category}
                            conditionLabels={conditionLabels}
                            conditionExpansion={conditionExpansion}
                            conditionRequiredFields={
                                conditionLabels.length
                                    ? conditionLabels.map(() => [])
                                    : missingRequiredFieldSet.has('query')
                                      ? [['query']]
                                      : []
                            }
                            deliveryLabels={deliveryLabels}
                            deliveryExpansion={deliveryExpansion}
                            deliveryRequiredFields={
                                deliveryLabels.length
                                    ? deliveryRequiredFields
                                    : missingRequiredFieldSet.has('delivery')
                                      ? [['delivery']]
                                      : []
                            }
                            description={description}
                            inspectorOpen={previewInspectorOpen}
                            match={match}
                            missingRequiredFields={missingRequiredFields}
                            name={name}
                            schedule={schedule}
                            selectedNodeId={selectedPreviewNode?.id}
                            onNodeSelect={setSelectedPreviewNode}
                            onInspectorOpenChange={setPreviewInspectorOpen}
                            onRemoveCondition={(index) => {
                                setConditions((current) => {
                                    if (current.length <= 1) {
                                        return current;
                                    }

                                    return current.filter((_, currentIndex) => currentIndex !== index);
                                });
                                setQueryError(undefined);
                            }}
                            onRemoveDelivery={(index) => {
                                if (deliveryMethods.length <= 1) {
                                    return;
                                }

                                const method = deliveryMethods[index];
                                if (method) {
                                    toggleDelivery(method, false);
                                }
                            }}
                            onSelectRequiredField={selectRequiredField}
                        />
                    </Col>
                    <DiagramDetailSider open={previewInspectorOpen}>
                        <AlertFlowNodeInspector
                            node={selectedPreviewNode}
                            onSelectRequiredField={selectRequiredField}
                        />
                    </DiagramDetailSider>
                </Row>
            )}
        </Form>
    );
};

export default AlertForm;
